import {makeUnknownComponentImportDiagnostic} from '@angular/compiler-cli/src/ngtsc/scope';
import {Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {AppConfigService} from '@ox/utils';
import {Dealer} from '@ox/typings';
import {TrackingService} from '@ox/data';
import {DealerMapService} from '../dealer-map/dealer-map.service';

@Component({
  selector: 'webu-dealer-directions',
  templateUrl: './dealer-directions-webu.component.html'
})
export class DealerDirectionsWebuComponent implements OnInit, OnChanges {
  @Input() profileAddress: string;
  @Input() dealer: Dealer;
  public directionsForm: FormGroup;
  public language = this.appConfig.bml.lang;
  public addressPattern = this.appConfig.fieldAttributes.address.address.pattern.source;
  public addressMaxLength = this.appConfig.fieldAttributes.address.address.maxlength;
  public addressCheckbox = true;
  public hideCheckbox = true;
  public showDirectionsText = false;
  public url = 'https://www.google.com/maps/';
  public addressNotFound: boolean;
  public map: any;
  public mapElement: HTMLElement;
  public directionsRenderer: any;
  public directionsRendererElement: HTMLElement;
  public dealerInfoElement: HTMLElement;

  constructor(
    private fb: FormBuilder,
    private appConfig: AppConfigService,
    private element: ElementRef,
    private dealerMapService: DealerMapService,
    private trackingService: TrackingService
  ) {
    this.directionsForm = this.fb.group({
      startingAddress: new FormControl('', [
        Validators.maxLength(this.addressMaxLength),
        Validators.pattern(this.addressPattern),
        Validators.required
      ]),
      dealerAddress: new FormControl('', [
        Validators.maxLength(this.addressMaxLength),
        Validators.pattern(this.addressPattern),
        Validators.required
      ])
    });
  }

  ngOnInit(): void {
    if(this.profileAddress) {
      this.hideCheckbox = false;
      this.directionsForm.controls['startingAddress'].setValue(this.profileAddress);
    }
    this.directionsRendererElement = this.element.nativeElement.querySelector('.js-dealer-directions-card');
    this.dealerInfoElement = this.element.nativeElement.querySelector('.js-dealer-info-pane');
    this.directionsRenderer = this.dealerMapService.Directions(this.directionsRendererElement, this.map);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['dealer']?.currentValue) {
      this.getDealerInfo();
    }
  }

  getDealerInfo() {
    if (!this.dealer || !this.dealer.id) return;
    if (!this.map) {
      this.mapElement = this.element.nativeElement.querySelector('.js-dealer-map-canvas');
      this.map = this.dealerMapService.DealerMap(this.mapElement);
    }
    this.showDirectionsText = false;
    this.directionsForm.controls['dealerAddress'].setValue(this.dealer.address);

    this.dealerMapService.setDealershipAddress(this.dealer.address);
    this.dealerMapService.mapMarker(this.dealer, this.map).subscribe({
      error: (err) => {
        console.warn('Dealer Map Directions: Failed to initialize the map. ', err);
      }
    });
  }

  getDirections() {
    const onFailure = () => {
      this.addressNotFound = true;
      this.trackingService.trackEvent('event20', 'noo.preferredDealer.directions.addressNotFound');
    };

    const onSuccess = () => {
      this.showDirectionsText = true;
      this.addressNotFound = false;
    };

    this.showDirectionsText = false;
    this.directionsRenderer.init().subscribe({
      next: () => {
        this.directionsRenderer.clearDirections();
        this.directionsRenderer.getDirections(this.directionsForm.controls['startingAddress'].value, this.directionsForm.controls['dealerAddress'].value);
      },
      catch: () => onFailure(),
      complete: () => onSuccess()
    });
  }

  printDirections() {
    const $windowScope = {
      google:  window.google,
      gm: window.gm,
      printDirectionsView: true,
      widget: {
        dealer: {...this.dealer}
      },
      directions: {
        dealerAddress: this.directionsForm.controls['dealerAddress'].value,
        startingAddress: this.directionsForm.controls['startingAddress'].value
      }
    };
    window.open('../preferredDealer/print', '_blank');
    window.$windowScope = $windowScope;
  }

  touchedStartingAddress() {
    if (this.directionsForm.controls['startingAddress'].value !== this.profileAddress) {
      this.addressCheckbox = false;
    } else {
      this.addressCheckbox = true;
    }
  }

  togglePopulateStartingAddress() {
    this.addressCheckbox = !this.addressCheckbox;
    if (this.addressCheckbox) {
      this.directionsForm.controls['startingAddress'].setValue(this.profileAddress);
      return;
    }
    this.directionsForm.controls['startingAddress'].setValue('');
  }

  openGoogleMaps() {
    if(!this.profileAddress || this.profileAddress === null || this.profileAddress === undefined){
      // sets url to display a map with only dealer address (this is used in the case the user doesn't input
      // anything into the input field for a starting address)
      // EXAMPLE URL: https://www.google.com/maps/search/?api=1&query=Google+Pyrmont+NSW
      this.url += 'search/?api=1&query=' + encodeURIComponent(this.dealer.address);
    }else{
      // sets url to display a map with directions to the dealer address
      // this will only execute if the user presses 'Get Directions' after inputting a starting address
      // EXAMPLE URL: https://www.google.com/maps/dir/?api=1&origin=Google+Pyrmont+NSW&destination=QVB
      this.url += 'dir/?api=1&origin=' + encodeURIComponent(this.profileAddress) +
      '&destination=' + encodeURIComponent(this.dealer.address);
    }
    window.open(this.url);
  }
}
